/**
 * ReauthModal.jsx - Inline Re-authentication Modal
 * 
 * Displays when a user's session expires while they're working.
 * Keeps the user on the current page (no navigation/redirect).
 * Allows them to re-authenticate without losing context.
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertCircle, Lock, RefreshCw } from 'lucide-react';

const ReauthModal = () => {
  const { showReauthModal, reauthReason, reauthenticate, dismissReauthModal, user, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill email if we have user info
  React.useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const result = await reauthenticate(email, password);
    
    setIsLoading(false);
    
    if (!result.success) {
      setError(result.error || 'Authentication failed');
    } else {
      // Success - modal will close automatically
      setPassword('');
    }
  };

  const handleLogoutClick = () => {
    dismissReauthModal();
    logout();
  };

  const getReasonMessage = () => {
    switch (reauthReason) {
      case 'refresh_token_expired':
        return 'Your session has expired. Please sign in again to continue.';
      case 'refresh_token_missing':
        return 'Session information was lost. Please sign in again.';
      case 'user_invalid':
        return 'Your account status has changed. Please sign in again.';
      default:
        return 'Your session needs to be renewed. Please sign in to continue.';
    }
  };

  return (
    <Dialog open={showReauthModal} onOpenChange={(open) => !open && dismissReauthModal()}>
      <DialogContent 
        className="sm:max-w-md"
        data-testid="reauth-modal"
        onInteractOutside={(e) => e.preventDefault()} // Prevent closing by clicking outside
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
              <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <DialogTitle className="text-xl">Session Expired</DialogTitle>
          </div>
          <DialogDescription className="text-left">
            {getReasonMessage()}
            <br />
            <span className="text-xs text-muted-foreground mt-1 block">
              Your work is safe. Sign in to continue where you left off.
            </span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {error && (
            <div 
              className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm"
              data-testid="reauth-error"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reauth-email">Email</Label>
            <Input
              id="reauth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={isLoading}
              data-testid="reauth-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reauth-password">Password</Label>
            <Input
              id="reauth-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={isLoading}
              autoFocus
              data-testid="reauth-password-input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              className="flex-1"
              disabled={isLoading}
              data-testid="reauth-submit-btn"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleLogoutClick}
              disabled={isLoading}
              data-testid="reauth-logout-btn"
            >
              Sign Out
            </Button>
          </div>
        </form>

        <p className="text-xs text-center text-muted-foreground mt-4">
          Having trouble? Contact your administrator.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default ReauthModal;
